import { GearSixIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router";

import { DashboardContent } from "../components/layout/dashboard-content";
import { UserAppearanceSettings } from "./appearance/appearance-settings";
import { SettingsPanel } from "./components/settings-panel";
import { SettingsTabs } from "./components/settings-tabs";
import { UserNotificationSettings } from "./notifications/notification-settings";
import { UserProfileSettings } from "./profile/profile-settings";
import { UserSecuritySettings } from "./security/security-settings";
import type { SettingsTabId } from "./types/settings";

const SETTINGS_TAB_IDS: SettingsTabId[] = ["profile", "security", "appearance", "notifications"];

const isSettingsTab = (value: string | null): value is SettingsTabId => {
  return value !== null && SETTINGS_TAB_IDS.includes(value as SettingsTabId);
};

export const UserSpaceSettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState<SettingsTabId>(
    isSettingsTab(tabFromUrl) ? tabFromUrl : "profile",
  );

  useEffect(() => {
    if (isSettingsTab(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const handleTabChange = (tab: SettingsTabId) => {
    setActiveTab(tab);

    setSearchParams(
      (currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);

        nextSearchParams.set("tab", tab);

        return nextSearchParams;
      },
      {
        replace: true,
      },
    );
  };

  return (
    <>
      <Helmet>
        <title>Paramètres - Mon CV Pro</title>
      </Helmet>

      <DashboardContent>
        <div className="mx-auto w-full max-w-[1240px]">
          <header className="mb-4 sm:mb-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 hidden size-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600 sm:grid">
                <GearSixIcon aria-hidden size={22} weight="duotone" />
              </span>

              <div>
                <h1 className="text-xl font-bold tracking-tight text-indigo-950 sm:text-2xl">
                  Paramètres
                </h1>

                <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-500 sm:text-[13px]">
                  Gérez votre profil, la sécurité de votre compte et vos préférences.
                </p>
              </div>
            </div>
          </header>

          <SettingsTabs activeTab={activeTab} onTabChange={handleTabChange} />

          <main className="mt-4 pb-24 lg:pb-6">
            <SettingsPanel tabId="profile" activeTab={activeTab}>
              <UserProfileSettings />
            </SettingsPanel>

            <SettingsPanel tabId="security" activeTab={activeTab}>
              <UserSecuritySettings />
            </SettingsPanel>

            <SettingsPanel tabId="appearance" activeTab={activeTab}>
              <UserAppearanceSettings />
            </SettingsPanel>

            <SettingsPanel tabId="notifications" activeTab={activeTab}>
              <UserNotificationSettings />
            </SettingsPanel>
          </main>
        </div>
      </DashboardContent>
    </>
  );
};
